import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { BaseInfoBlock } from '@/delivery-type-plugin/capabilities/base-info/BaseInfoBlock';
import { resolveBaseInfoPaths } from '@/delivery-type-plugin/capabilities/base-info/resolvePaths';
import { EnvVarsBlock } from '@/delivery-type-plugin/capabilities/env-vars';
import { HealthCheckBlock } from '@/delivery-type-plugin/capabilities/health-check';
import { HEALTH_CHECK_FIELD } from '@/delivery-type-plugin/capabilities/health-check/fieldKeys';
import type { MaasOptions } from '@/delivery-type-plugin/capabilities/maas-init';
import { MaasGroupsConfigSection } from '@/delivery-type-plugin/capabilities/maas-node-groups';
import { RuntimeConfigBlock } from '@/delivery-type-plugin/capabilities/runtime-config';
import { resolveRuntimeConfigPaths } from '@/delivery-type-plugin/capabilities/runtime-config/resolvePaths';
import { APP_INSTANCE_USAGE_SCENARIO_OPTIONS, ServiceBlock } from '@/delivery-type-plugin/capabilities/service';
import { ModelStepSection } from '@/delivery-type-plugin/plugins';
import styles from '@/delivery-type-plugin/styles/maas-model-step.module.less';

import type { CapabilityBinding, DeployAccessPolicy, DeployResourceEditorProps } from '../../../core/types';
import { buildCapabilityControl } from '../../../platform/capability-registry/buildCapabilityControl';
import { renderCapabilityModules } from '../../../platform/capability-registry/renderCapabilityModules';
import { OptionMetaRootField } from '../../../platform/option-meta';
import type { DeployScene } from '../../../platform/scenes/types';
import type { AppInstanceFieldKey } from '../fields';
import { getAppStorageSubmitError, readApplicationInstanceFormValues } from '../headless/payload';
import type { AppInstanceLinkagePropsMap } from '../linkage';
import { useAppInstanceDeployFormLinkage } from '../linkage/useAppInstanceDeployFormLinkage';
import { APP_INSTANCE_MODULES } from '../modules';
import { resolveAppInstanceSceneConfig } from '../scenes';
import type { ApplicationInstanceFormValues } from '../types';
import { AppInstanceServiceMountFields } from './AppInstanceServiceMountFields';
import { AppStorageSetting } from './AppStorageSetting';
import { ApplicationImageSetting } from './ApplicationImageSetting';
import { adaptAppInstanceAccessPolicy } from './accessPolicyBridge';

export { useAppInstanceDeployFormLinkage };

const FormItem = Form.Item;
const APP_STORAGE_CONFIGURED_FIELD = 'appStorageConfigured';

const defaultPolicy: DeployAccessPolicy<AppInstanceFieldKey> = {
  field: () => 'editable',
  action: () => 'enabled',
};

function findAppCapabilityBinding(moduleKey: string, capabilityKey: string): CapabilityBinding | undefined {
  const mod = APP_INSTANCE_MODULES.find((item) => item.key === moduleKey) as { capabilities: readonly CapabilityBinding[] } | undefined;
  return mod?.capabilities.find((binding) => String(binding.key) === capabilityKey);
}

export type AppInstanceMaasOptions = MaasOptions;

export interface AppInstanceResourceEditorProps extends DeployResourceEditorProps<AppInstanceFieldKey> {
  scene?: DeployScene | 'direct' | 'sku' | 'order';
  namespace?: string;
  typeOptions?: AppInstanceMaasOptions;
  isEdit?: boolean;
  initValues?: ApplicationInstanceFormValues;
  /** 是否展示存储区块（下单 fixed 等场景由 L3 探测） */
  showStorageSection?: boolean;
  presetArchitecture?: string;
  presetImageLocked?: boolean;
  lockActionsWhenFilled?: boolean;
  /** SKU 外层表单（存储盘创建弹窗算价） */
  skuOuterForm?: FormInstance;
  /** SKU pages context 与 platform 非同实例时需显式传入 */
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  /** 数据盘弹窗确认后询价 */
  onPriceChange?: (outerValues: Record<string, unknown>, innerForm: FormInstance) => void | Promise<void>;
  /** 与 shell / order 对齐；节点组计价等经 capabilityOptions 注入 */
  capabilityOptions?: Partial<Record<string, Record<string, unknown>>>;
  skuType?: 'fixed' | 'custom';
  /** 父级统一 linkage 时注入，避免多 Editor 重复订阅 */
  linkageProps?: Partial<AppInstanceLinkagePropsMap>;
  /** 覆盖默认应用镜像 UI */
  renderAppImage?: (ctx: { form: FormInstance }) => ReactNode;
  /** 覆盖默认应用存储 UI */
  renderAppStorage?: (ctx: { form: FormInstance }) => ReactNode;
  /** 包裹各模块内容（如 ResourceEditGuard） */
  wrapModule?: (moduleKey: string, resourceSection: boolean, children: ReactNode) => ReactNode;
}

/** 应用镜像字段：仅镜像/框架（envVars / service 已拆为独立勾选模块） */
export function AppInstanceAppServiceFields({
  form,
  renderAppImage,
  isEdit,
  initValues,
  presetArchitecture,
  presetImageLocked,
  lockActionsWhenFilled,
}: {
  form: FormInstance;
  renderAppImage?: (ctx: { form: FormInstance }) => ReactNode;
  isEdit?: boolean;
  initValues?: ApplicationInstanceFormValues;
  presetArchitecture?: string;
  presetImageLocked?: boolean;
  lockActionsWhenFilled?: boolean;
}) {
  const imageNode = renderAppImage?.({ form }) ?? (
    <ApplicationImageSetting
      form={form}
      compact
      isEdit={isEdit}
      initValues={initValues}
      presetArchitecture={presetArchitecture}
      presetImageLocked={presetImageLocked}
      lockActionsWhenFilled={lockActionsWhenFilled}
    />
  );

  return <div className={styles.compactForm}>{imageNode}</div>;
}

/** 公共环境变量独立模块（仅 ResourceEditor 内用） */
function AppInstanceEnvVarsFields({
  form,
  accessPolicy = defaultPolicy,
}: {
  form: FormInstance;
  accessPolicy?: DeployAccessPolicy<AppInstanceFieldKey>;
}) {
  const adaptedPolicy = adaptAppInstanceAccessPolicy(accessPolicy);
  const envBinding = findAppCapabilityBinding('envVars', 'env-vars');
  const envControl = envBinding ? buildCapabilityControl(envBinding, adaptedPolicy) : { fieldModes: {}, actionModes: {} };

  return (
    <div className={styles.compactForm}>
      <EnvVarsBlock form={form} paths={{ list: 'commonEnv' }} options={{ useCard: false }} blockMode="edit" {...envControl} />
    </div>
  );
}

/** 服务配置独立模块：服务端口 + 入口节点 */
function AppInstanceServiceFields({
  form,
  accessPolicy = defaultPolicy,
}: {
  form: FormInstance;
  accessPolicy?: DeployAccessPolicy<AppInstanceFieldKey>;
}) {
  const adaptedPolicy = adaptAppInstanceAccessPolicy(accessPolicy);
  const serviceBinding = findAppCapabilityBinding('service', 'service');
  const serviceControl = serviceBinding ? buildCapabilityControl(serviceBinding, adaptedPolicy) : { fieldModes: {}, actionModes: {} };

  return (
    <div className={styles.compactForm}>
      <ServiceBlock
        form={form}
        paths={{}}
        options={{ valueFormat: 'json-string', usageScenarioOptions: APP_INSTANCE_USAGE_SCENARIO_OPTIONS }}
        blockMode="edit"
        {...serviceControl}
      />
      <AppInstanceServiceMountFields form={form} />
    </div>
  );
}

/** 节点组字段 */
export function AppInstanceNodeGroupsFields({
  form,
  namespace,
  typeOptions,
  isEdit,
  initValues,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  lockActionsWhenFilled,
}: {
  form: FormInstance;
  namespace?: string;
  typeOptions?: AppInstanceMaasOptions;
  isEdit?: boolean;
  initValues?: ApplicationInstanceFormValues;
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  lockActionsWhenFilled?: boolean;
}) {
  return (
    <MaasGroupsConfigSection
      form={form}
      namespace={namespace || ''}
      isEdit={isEdit}
      initValues={initValues}
      typeOptions={typeOptions}
      billModelId={billModelId}
      deploymentTypeId={deploymentTypeId}
      paymentTiming={paymentTiming}
      skuType={skuType}
      resourceEditMode={resourceEditMode}
      lockActionsWhenFilled={lockActionsWhenFilled}
      initCommandEditor="startup"
    />
  );
}

/** 应用存储字段（含提交前校验 FormItem）；默认内置 AppStorageSetting */
export function AppInstanceStorageFields({
  form,
  renderAppStorage,
  skuOuterForm,
  billModelId,
  deploymentTypeId,
  resourceEditMode,
  onPriceChange,
  lockActionsWhenFilled,
  initValues,
}: {
  form: FormInstance;
  renderAppStorage?: (ctx: { form: FormInstance }) => ReactNode;
  skuOuterForm?: FormInstance;
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  resourceEditMode?: string;
  onPriceChange?: (outerValues: Record<string, unknown>, innerForm: FormInstance) => void | Promise<void>;
  lockActionsWhenFilled?: boolean;
  initValues?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const storageNode = renderAppStorage?.({ form }) ?? (
    <AppStorageSetting
      form={form}
      skuOuterForm={skuOuterForm}
      billModelId={billModelId}
      deploymentTypeId={deploymentTypeId}
      resourceEditMode={resourceEditMode}
      onPriceChange={onPriceChange}
      lockActionsWhenFilled={lockActionsWhenFilled}
      initValues={initValues}
    />
  );

  return (
    <div className={styles.compactForm}>
      <FormItem
        field={APP_STORAGE_CONFIGURED_FIELD}
        rules={[
          {
            validator(_, callback) {
              const values = readApplicationInstanceFormValues(form);
              const error = getAppStorageSubmitError(values);
              if (error) {
                callback(error.fieldKey ? t(error.key, { name: t(error.fieldKey) }) : t(error.key));
                return;
              }
              callback();
            },
          },
        ]}
        labelCol={{ span: 0 }}
        wrapperCol={{ span: 24 }}
        style={{ marginBottom: 0 }}
      >
        {storageNode}
      </FormItem>
    </div>
  );
}

/**
 * AppInstance ResourceEditor：与 VM 同构（scene + linkage 注入 + renderCapabilityModules）。
 * 镜像 / 存储 / 节点组等仍走定制 UI（renderModuleExtras），不渲染 registry 默认 Block（字段路径不同）。
 */
export function AppInstanceResourceEditor(props: AppInstanceResourceEditorProps) {
  if (props.linkageProps !== undefined) {
    return <AppInstanceResourceEditorView {...props} linkageProps={props.linkageProps} />;
  }
  return <AppInstanceResourceEditorWithLinkage {...props} />;
}

function AppInstanceResourceEditorWithLinkage(props: Omit<AppInstanceResourceEditorProps, 'linkageProps'>) {
  const sceneConfig = useMemo(() => {
    if (props.scene === 'sku' || props.scene === 'order') return resolveAppInstanceSceneConfig(props.scene);
    return resolveAppInstanceSceneConfig('direct');
  }, [props.scene]);
  const linkageEnabled = sceneConfig.linkage !== false;
  const linkageProps = useAppInstanceDeployFormLinkage(props.form, linkageEnabled);
  return <AppInstanceResourceEditorView {...props} linkageProps={linkageProps} />;
}

function AppInstanceResourceEditorView({
  form,
  modules: moduleFilter,
  scene,
  accessPolicy = defaultPolicy,
  moduleSelection,
  namespace,
  typeOptions,
  isEdit,
  initValues,
  showStorageSection = true,
  presetArchitecture,
  presetImageLocked,
  lockActionsWhenFilled,
  skuOuterForm,
  billModelId,
  deploymentTypeId,
  onPriceChange,
  capabilityOptions,
  skuType,
  linkageProps = {},
  renderAppImage,
  renderAppStorage,
  wrapModule,
}: AppInstanceResourceEditorProps & { linkageProps: Partial<AppInstanceLinkagePropsMap> }) {
  const { t } = useTranslation();
  const sceneConfig = useMemo(() => {
    if (scene === 'sku' || scene === 'order') return resolveAppInstanceSceneConfig(scene);
    return resolveAppInstanceSceneConfig('direct');
  }, [scene]);
  const resolvedModules = moduleFilter ?? sceneConfig.modules;
  const healthFieldMode = accessPolicy.field('app.healthCheck');

  const visibleModules = useMemo(() => {
    return APP_INSTANCE_MODULES.filter((mod) => {
      if (!resolvedModules.includes(mod.key)) return false;
      if (mod.key === 'storage' && !showStorageSection) return false;
      if (mod.key === 'healthCheck' && healthFieldMode === 'hidden') return false;
      return true;
    });
  }, [healthFieldMode, resolvedModules, showStorageSection]);

  /** 外层已有 SkuModuleCheckboxSection 标题时，不再套 ModelStepSection */
  const embedInOuterModuleChrome = resolvedModules.length === 1;

  const nodeGroupsPricing = (capabilityOptions?.['maas-node-groups'] ?? {}) as {
    billModelId?: string;
    deploymentTypeId?: string;
    paymentTiming?: string;
    skuType?: string;
    resourceEditMode?: string;
    lockActionsWhenFilled?: boolean;
  };
  const resolvedBillModelId = billModelId ?? nodeGroupsPricing.billModelId;
  const resolvedDeploymentTypeId = deploymentTypeId ?? nodeGroupsPricing.deploymentTypeId;
  const resolvedPaymentTiming = nodeGroupsPricing.paymentTiming;
  const resolvedSkuType = skuType ?? nodeGroupsPricing.skuType;
  const resolvedResourceEditMode = nodeGroupsPricing.resourceEditMode;
  const resolvedLockActionsWhenFilled = lockActionsWhenFilled ?? nodeGroupsPricing.lockActionsWhenFilled;

  const adaptedPolicy = useMemo(() => adaptAppInstanceAccessPolicy(accessPolicy), [accessPolicy]);
  const mergedCapabilityDefaults = useMemo(
    () => ({ ...sceneConfig.capabilityOptions, ...capabilityOptions }),
    [capabilityOptions, sceneConfig.capabilityOptions],
  );

  const wrap = (moduleKey: string, resourceSection: boolean, children: ReactNode) =>
    wrapModule ? wrapModule(moduleKey, resourceSection, children) : children;

  const healthBlockMode = healthFieldMode === 'readonly' || healthFieldMode === 'disabled' ? 'preview' : 'edit';

  return (
    <div className={styles.modelStep}>
      <OptionMetaRootField />
      {moduleSelection}
      {renderCapabilityModules({
        form,
        modules: visibleModules,
        accessPolicy: adaptedPolicy,
        capabilityOptions: mergedCapabilityDefaults,
        linkageProps,
        renderModuleExtras: (moduleKey) => {
          const withSection = (key: string, title: string, node: ReactNode) =>
            embedInOuterModuleChrome
              ? [<div key={key}>{node}</div>]
              : [
                  <ModelStepSection key={key} title={title}>
                    {node}
                  </ModelStepSection>,
                ];

          // App 表单路径与 registry Block 默认 paths 不一致：忽略 capabilityNodes，仅用定制 UI。
          if (moduleKey === 'baseInfo') {
            const baseInfoOptions = (mergedCapabilityDefaults['base-info'] ?? {}) as {
              isEdit?: boolean;
              allowEditName?: boolean;
              hideWorkspace?: boolean;
              nameField?: 'alias';
              nameMaxLength?: number;
              descriptionMaxLength?: number | false;
              descriptionRows?: number;
            };
            const baseInfoBlockOptions = {
              nameField: baseInfoOptions.nameField ?? ('alias' as const),
              nameMaxLength: baseInfoOptions.nameMaxLength ?? 64,
              descriptionMaxLength: baseInfoOptions.descriptionMaxLength ?? false,
              descriptionRows: baseInfoOptions.descriptionRows ?? 8,
              isEdit: baseInfoOptions.isEdit ?? isEdit,
              allowEditName: baseInfoOptions.allowEditName,
              hideWorkspace: baseInfoOptions.hideWorkspace,
            };
            return withSection(
              'baseInfo',
              t('data-center:maas.step.basic'),
              wrap(
                'baseInfo',
                false,
                <BaseInfoBlock
                  form={form}
                  paths={resolveBaseInfoPaths('', baseInfoBlockOptions)}
                  options={baseInfoBlockOptions}
                  blockMode="edit"
                  fieldModes={{}}
                  actionModes={{}}
                />,
              ),
            );
          }

          if (moduleKey === 'runtimeConfig') {
            const runtimeOptions = (mergedCapabilityDefaults['runtime-config'] ?? {}) as {
              isEdit?: boolean;
              locked?: boolean;
              typeOptions?: MaasOptions;
              runtimeTypeOnly?: boolean;
            };
            return withSection(
              'runtimeConfig',
              t('data-center:maas.runtime.config'),
              wrap(
                'runtimeConfig',
                false,
                <RuntimeConfigBlock
                  form={form}
                  paths={resolveRuntimeConfigPaths('')}
                  options={{
                    isEdit: runtimeOptions.isEdit ?? isEdit,
                    locked: runtimeOptions.locked,
                    typeOptions: runtimeOptions.typeOptions ?? typeOptions,
                    runtimeTypeOnly: runtimeOptions.runtimeTypeOnly,
                  }}
                  blockMode="edit"
                  fieldModes={{}}
                  actionModes={{}}
                />,
              ),
            );
          }

          if (moduleKey === 'appService') {
            return withSection(
              'appService',
              t('data-center:maas.app.image'),
              wrap(
                'appService',
                false,
                <AppInstanceAppServiceFields
                  form={form}
                  renderAppImage={renderAppImage}
                  isEdit={isEdit}
                  initValues={initValues}
                  presetArchitecture={presetArchitecture}
                  presetImageLocked={presetImageLocked}
                  lockActionsWhenFilled={lockActionsWhenFilled}
                />,
              ),
            );
          }

          if (moduleKey === 'envVars') {
            return withSection(
              'envVars',
              t('data-center:maas.common.env.vars'),
              wrap('envVars', false, <AppInstanceEnvVarsFields form={form} accessPolicy={accessPolicy} />),
            );
          }

          if (moduleKey === 'service') {
            return withSection(
              'service',
              t('data-center:maas.section.service'),
              wrap('service', false, <AppInstanceServiceFields form={form} accessPolicy={accessPolicy} />),
            );
          }

          if (moduleKey === 'healthCheck') {
            const healthCheckNode = (
              <div className={styles.compactForm}>
                <HealthCheckBlock form={form} paths={{ config: HEALTH_CHECK_FIELD }} blockMode={healthBlockMode} />
              </div>
            );
            return withSection('healthCheck', t('data-center:probe'), wrap('healthCheck', false, healthCheckNode));
          }

          if (moduleKey === 'nodeGroups') {
            return withSection(
              'nodeGroups',
              t('data-center:maas.step.groups'),
              wrap(
                'nodeGroups',
                true,
                <AppInstanceNodeGroupsFields
                  form={form}
                  namespace={namespace}
                  typeOptions={typeOptions}
                  isEdit={isEdit}
                  initValues={initValues}
                  billModelId={resolvedBillModelId}
                  deploymentTypeId={resolvedDeploymentTypeId}
                  paymentTiming={resolvedPaymentTiming}
                  skuType={resolvedSkuType}
                  resourceEditMode={resolvedResourceEditMode}
                  lockActionsWhenFilled={resolvedLockActionsWhenFilled}
                />,
              ),
            );
          }

          if (moduleKey === 'storage') {
            return withSection(
              'storage',
              t('data-center:maas.section.storage'),
              wrap(
                'storage',
                true,
                <AppInstanceStorageFields
                  form={form}
                  renderAppStorage={renderAppStorage}
                  skuOuterForm={skuOuterForm}
                  billModelId={resolvedBillModelId}
                  deploymentTypeId={resolvedDeploymentTypeId}
                  resourceEditMode={resolvedResourceEditMode}
                  onPriceChange={onPriceChange}
                  lockActionsWhenFilled={resolvedLockActionsWhenFilled}
                  initValues={initValues as Record<string, unknown> | undefined}
                />,
              ),
            );
          }

          return [];
        },
      })}
    </div>
  );
}
